'use client';

import { TravelReviewCGType } from '@/types/graphql/root';
import { useEffect, useMemo, useRef, useState } from 'react';
import LinkForZzimcarAndroid from '../../common/link-for-zzimcar-android';
import { filterNullUndefined } from '@/utils';
import { useSetAtom } from 'jotai';
import { historyStackAtom } from '@/atom/historyStack';
import { history } from '@/utils/history';
import { usePathname, useRouter } from 'next/navigation';
import SharingTripImageModal from './sharing-trip-image-modal';
import ArrowDown138 from '@svg/sharing-trips/arrow_down_13_8.svg';
import ArrowRightPrimary610 from '@svg/sharing-trips/arrow_right_primary_6_10.svg';

export default function SharingTripPostContentContainer({ post }: { post: TravelReviewCGType }) {
  const pathname = usePathname();
  const setHistoryStack = useSetAtom(historyStackAtom);

  const imageContainerRef = useRef<HTMLDivElement>(null);

  const [isOpenItinerary, setIsOpenItinerary] = useState(false);
  const [isOpenImageModal, setIsOpenImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const poiImages = useMemo(() => {
    return filterNullUndefined(post.images);
  }, [post.images]);

  useEffect(() => {
    if (!imageContainerRef.current) return;

    let isDragging = false;
    let mouseDownX = 0;
    let currentScrollLeft = 0;
    let startTime = 0;
    let movedDistance = 0;

    const mouseDownHandler = (e: MouseEvent) => {
      e.preventDefault();
      isDragging = true;
      mouseDownX = e.clientX;
      currentScrollLeft = imageContainerRef.current!.scrollLeft;
      startTime = Date.now();
      movedDistance = 0;
    };

    const mouseUpHandler = (e: MouseEvent) => {
      e.preventDefault();

      // 클릭으로 간주할 조건:
      // 1. 마우스를 누른 시간이 200ms 이하
      // 2. 마우스 이동 거리가 5px 이하
      const endTime = Date.now();
      const timeElapsed = endTime - startTime;

      if (isDragging && timeElapsed < 200 && Math.abs(movedDistance) < 5) {
        // 클릭 이벤트 처리
        const target = e.target as HTMLElement;
        if (target.tagName === 'IMG') {
          // 이미지 클릭 핸들러
          handleImageClick(target);
        }
      }

      isDragging = false;
    };

    const mouseMoveHandler = (e: MouseEvent) => {
      e.preventDefault();
      if (!isDragging) return;

      const deltaX = e.clientX - mouseDownX;
      movedDistance = deltaX; // 이동 거리 기록
      imageContainerRef.current!.scrollLeft = currentScrollLeft - deltaX;
    };

    // 이미지 클릭 핸들러 함수
    const handleImageClick = (imgElement: HTMLElement) => {
      // 여기에 이미지 클릭 시 실행할 코드 작성
      // 예: 이미지 모달 열기, 상세 보기 등
      const index = imgElement.getAttribute('data-index');
      if (!index) return;
      if (!Number.isInteger(Number(index))) return;

      setSelectedImageIndex(Number(index));
      setIsOpenImageModal(true);

      history?.push(pathname);
      setHistoryStack((prev) => [
        ...prev,
        {
          name: '이미지 전체화면 모달 닫기 (from sharing-trip-post-content-container)',
          afterAction: () => {
            setIsOpenImageModal(false);
            setSelectedImageIndex(null);
          },
        },
      ]);
    };

    imageContainerRef.current.addEventListener('mousedown', mouseDownHandler);
    document.addEventListener('mouseup', mouseUpHandler);
    document.addEventListener('mousemove', mouseMoveHandler);

    return () => {
      imageContainerRef.current?.removeEventListener('mousedown', mouseDownHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
      document.removeEventListener('mousemove', mouseMoveHandler);
    };
  }, [imageContainerRef.current]);

  return (
    <>
      <div className='whitespace-pre-line body-14-regular text-new-gray-800 px-20pxr pb-16pxr'>{post.content}</div>

      {poiImages.length > 0 && (
        <div ref={imageContainerRef} id='post-images-container' className='px-20pxr flex gap-8pxr overflow-x-scroll'>
          {poiImages.map((image, index) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              data-index={index}
              key={image}
              src={image}
              alt='poi'
              className='cursor-pointer shrink-0 w-140pxr h-140pxr object-cover rounded-8pxr post-image'
            />
          ))}
        </div>
      )}

      <div className='pt-40pxr px-20pxr flex flex-col gap-8pxr pb-16pxr'>
        <div
          className={`w-full rounded-8pxr border border-new-gray-300 transition-all overflow-hidden ${isOpenItinerary ? 'h-750pxr' : 'h-295pxr'}`}
        >
          <iframe
            src={`/trip/${post.surveyId}?embedded=t`}
            className={`w-full h-750pxr overflow-hidden`}
            sandbox='allow-same-origin allow-scripts allow-popups'
            loading='lazy'
          ></iframe>
        </div>
        <button
          className='w-full h-44pxr flex items-center justify-center gap-2pxr border border-new-gray-300 rounded-8pxr'
          onClick={() => setIsOpenItinerary(!isOpenItinerary)}
        >
          <span className='body-14-regular text-new-gray-700'>일정표 {isOpenItinerary ? '접기' : '펼치기'}</span>
          <span className='w-20pxr h-20pxr flex items-center justify-center'>
            <ArrowDown138 className={`transition-all ${isOpenItinerary ? 'rotate-180' : ''}`} />
          </span>
        </button>
      </div>

      <div className='w-full px-20pxr'>
        <div className='w-full p-20pxr flex flex-col gap-8pxr bg-new-point-50 rounded-8pxr'>
          <div className='flex flex-col gap-4pxr'>
            <span className='body-14-md text-new-gray-900'>이 일정표가 마음에 드시나요?</span>
            <span className='body-14-regular text-new-gray-800'>{'일정표를 열어 나만의 일정표로 복사해보세요 :)'}</span>
          </div>
          <div className='w-full flex items-center justify-end'>
            <LinkForZzimcarAndroid
              href={`/trip/${post.surveyId}`}
              target='_blank'
              className='flex gap-2pxr body-12-md text-new-point-500'
            >
              일정표 열기
              <span className='w-16pxr h-16pxr flex items-center justify-center'>
                <ArrowRightPrimary610 />
              </span>
            </LinkForZzimcarAndroid>
          </div>
        </div>
      </div>

      {isOpenImageModal && selectedImageIndex !== null && (
        <SharingTripImageModal selectedImageIndex={selectedImageIndex} poiImages={poiImages} />
      )}
    </>
  );
}
